import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { hasModuleFromToken } from "../../../utils/authToken";

import { DP_EMPLOYEES_PAGE } from "../../dp/routes";
import { HOMEPAGE } from "../../home/routes";
import { ADMIN_CONFIGURACOES_PAGE, ADMIN_CONSUMO_IA_PAGE, ADMIN_LOGS_PAGE, ADMIN_USUARIOS_PAGE } from "../routes";
import "../../dp/pages/DpEco.css";

type ModuleCard = {
  title: string;
  description: string;
  icon: EcoIconName;
  onClick: () => void;
};

export default function AdminSistemaPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(HOMEPAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  // Atalho para o DP some quando o módulo foi retirado do administrador restrito (ADR 0027).
  const canDp = hasModuleFromToken(token, "DP");

  const sections = useMemo<{ id: string; title: string; modules: ModuleCard[] }[]>(
    () => [
      {
        id: "acesso",
        title: "Acesso",
        modules: [
          {
            title: "Gestão de usuários",
            description: "Usuários do sistema e freelancers: criar, editar, desativar e importar por planilha.",
            onClick: () => navigate(ADMIN_USUARIOS_PAGE()),
            icon: "shield",
          },
          ...(canDp ? [{
            // Colaborador CLT não é conta de sistema: o acesso dele nasce do
            // cadastro de DP. Este card é um ATALHO para lá, não uma segunda porta
            // de gestão — é a decisão registrada no ADR 0009.
            title: "Colaboradores CLT",
            description: "O acesso do colaborador é gerido no Departamento Pessoal; este card leva até lá.",
            onClick: () => navigate(DP_EMPLOYEES_PAGE()),
            icon: "users" as EcoIconName,
          }] : []),
        ],
      },
      {
        id: "sistema",
        title: "Sistema",
        modules: [
          {
            title: "Configurações do sistema",
            description: "Defina como a aplicação se comporta: metas, obrigatoriedades e módulos opcionais.",
            onClick: () => navigate(ADMIN_CONFIGURACOES_PAGE()),
            icon: "gear",
          },
          {
            title: "Logs de auditoria",
            description: "Quem fez o quê, quando, em qual registro e de onde — tudo o que alterou dado no sistema.",
            onClick: () => navigate(ADMIN_LOGS_PAGE()),
            icon: "clock",
          },
          {
            title: "Consumo de IA",
            description: "Quem usou inteligência artificial, para quê, quando e quanto custou, com o limite do mês.",
            onClick: () => navigate(ADMIN_CONSUMO_IA_PAGE()),
            icon: "barChart",
          },
        ],
      },
    ],
    [navigate, canDp]
  );

  if (!token) return null;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate(HOMEPAGE()) }, { label: "Administração do Sistema" }]}
          title="Administração do Sistema"
          subtitle="Contas de acesso, atalho para os colaboradores CLT, configurações da aplicação, logs de auditoria e consumo de IA."
          onBack={goBack}
          backLabel="Voltar ao início"
        />

        {sections.map((section) => (
          <section key={section.id} className="dpeSection" aria-labelledby={`adm-${section.id}`}>
            <h2 id={`adm-${section.id}`} className="dpeSection__title">{section.title}</h2>
            <div className="dpeModules">
              {section.modules.map((m) => (
                <button key={m.title} type="button" className="dpeModule" onClick={m.onClick}>
                  <span className="dpeModule__icon">
                    <EcoIcon name={m.icon} size={20} strokeWidth={1.8} />
                  </span>
                  <span className="dpeModule__text">
                    <span className="dpeModule__title">{m.title}</span>
                    <span className="dpeModule__desc">{m.description}</span>
                  </span>
                  <EcoIcon name="chevronRight" className="dpeModule__chevron" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
